import { useState, useMemo, useRef, useEffect, type FormEvent } from 'react';
import { parseCapture } from '@/lib/capture.ts';
import {
  duePreset,
  matchDuePreset,
  DUE_PRESETS,
  DUE_PRESET_LABELS,
  type DuePreset,
} from '@/lib/dates.ts';
import { useCreateTask } from '@/hooks/useTasks.ts';
import { Chip } from './ui.tsx';
import { DateField } from './DateField.tsx';
import { PRIORITY_LABELS } from '@shared/types';
import type { TaskStatus } from '@shared/types';

interface Props {
  defaultStatus?: TaskStatus;
  defaultProjectId?: string | null;
  defaultDue?: DuePreset | null;
  placeholder?: string;
}

export function QuickCapture({
  defaultStatus = 'inbox',
  defaultProjectId = null,
  defaultDue = null,
  placeholder = 'Add a task',
}: Props) {
  const [text, setText] = useState('');
  const [override, setOverride] = useState<Date | null | undefined>(undefined);
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const createTask = useCreateTask();

  const parsed = useMemo(() => parseCapture(text), [text]);
  const effectiveDue =
    override !== undefined ? override : (parsed.dueAt ?? (defaultDue ? duePreset(defaultDue) : null));
  const activePreset = matchDuePreset(effectiveDue);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      const t = e.target as HTMLElement | null;
      const typing =
        t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement || t?.isContentEditable;
      if (e.key === '/' && !typing) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!parsed.title.trim()) return;

    createTask.mutate(
      {
        title: parsed.title,
        status: defaultStatus,
        priority: parsed.priority,
        due_at: effectiveDue?.toISOString() ?? null,
        tag_names: parsed.tags,
        ...(parsed.project
          ? { project_name: parsed.project }
          : defaultProjectId
            ? { project_id: defaultProjectId }
            : {}),
      },
      {
        onSuccess: () => {
          setText('');
          setOverride(undefined);
        },
      },
    );
  }

  // The controls only appear once the composer is in use. They were permanent
  // onboarding text on all seven screens before — instructions that never went
  // away stop being read and start being furniture.
  const expanded = focused || text.length > 0;

  return (
    <form onSubmit={handleSubmit} className="mb-6">
      <div
        className={`flex items-center gap-2 rounded-sm border bg-inset px-3 transition-colors duration-[120ms] ease-out ${
          expanded ? 'border-brass' : 'border-line hover:border-line-strong'
        }`}
      >
        <svg viewBox="0 0 12 12" aria-hidden="true" className="size-3 shrink-0 text-ink-3">
          <path d="M6 1v10M1 6h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
        <input
          ref={inputRef}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            if (parseCapture(e.target.value).dueAt) setOverride(undefined);
          }}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder={placeholder}
          aria-label="Add a task"
          className="w-full bg-transparent py-2.5 text-ink placeholder:text-ink-3 focus:outline-none"
        />
        {text.length === 0 && (
          <kbd className="hidden shrink-0 rounded-xs border border-line px-1.5 py-0.5 text-[10px] text-ink-3 sm:block">
            /
          </kbd>
        )}
      </div>

      {expanded && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {DUE_PRESETS.map((preset) => (
            <Chip
              key={preset}
              active={activePreset === preset}
              onClick={() => setOverride(duePreset(preset))}
            >
              {DUE_PRESET_LABELS[preset]}
            </Chip>
          ))}

          <span className="w-32">
            <DateField
              value={effectiveDue?.toISOString() ?? null}
              onChange={(iso) => setOverride(iso ? new Date(iso) : null)}
              ariaLabel="Pick a due date"
            />
          </span>

          {parsed.priority > 0 && <Chip active>{PRIORITY_LABELS[parsed.priority]}</Chip>}
          {parsed.project && <Chip active>+{parsed.project}</Chip>}
          {parsed.tags.map((tag) => (
            <Chip key={tag} active>
              #{tag}
            </Chip>
          ))}

          <span className="t-meta ml-auto hidden text-ink-3 sm:block">
            tomorrow 3pm · #tag · !high · +project
          </span>
        </div>
      )}

      {createTask.isError && (
        <p role="alert" className="t-meta mt-2 text-brick">
          Could not add that. Check the connection and try again.
        </p>
      )}
    </form>
  );
}
